import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  Suspense,
  lazy,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiFetch, useApp } from "../../context/AppContext";
import {
  Card,
  Button,
  Modal,
  Input,
  Select,
  Badge,
  EmptyState,
  Pagination,
} from "../../components/common/UI";
import FormQuestionsStatGrid from "../../components/common/FormQuestionsStatGrid";
import FormQuestionsBulkUploadModal from "../../components/common/FormQuestionsBulkUploadModal";
import { FORM_SECTIONS } from "../../data/formQuestionSections";
import { getDpdpPolicies } from "../../data/dpdpPolicies";
import { getPolicyIdsForQuestion } from "../../data/gapPolicyMapping";
import { confirmAction } from "../../utils/sweetAlert";
import QuestionActivityLogModal from "../../components/common/QuestionActivityLogModal";
import DeletedQuestionsLogModal from "../../components/common/DeletedQuestionsLogModal";
import {
  Plus,
  Edit2,
  Trash2,
  Upload,
  Search,
  ClipboardCheck,
  FileText,
  Shield,
  ListChecks,
  ClipboardList,
  RotateCw,
  Crown,
  ArrowUpRight,
  GripVertical,
  ChevronDown,
  SlidersHorizontal,
} from "lucide-react";
import {
  referenceEvidencePreviewPath,
} from "../../components/common/gapRecommendationUtils";
import {
  ReferenceEvidenceAdminCard,
  ReferenceEvidenceViewModal,
  ReferenceEvidenceTableCell,
  buildEvidenceCatalogMap,
  lookupEvidenceCatalogItem,
} from "../../components/common/GapQuestionComment";
import { STORED_EVIDENCE_TABLE_COLUMN } from "../../components/common/gapRecommendationUtils";
import { openAuthenticatedAttachment } from "../../context/AppContext";

const GAP_SECTION_CODES = {
  notice: "NOT",
  consent: "CON",
  purpose: "PUR",
  data_minimisation: "DMN",
  storage: "STG",
  accuracy: "ACC",
  security: "SEC",
  rights: "DPR",
  accountability: "GOV",
  breach: "BRM",
};

function nextGapQuestionId(sectionId, items = []) {
  const prefix =
    GAP_SECTION_CODES[sectionId] || sectionId.toUpperCase().slice(0, 3);
  let maxNum = 0;
  for (const item of items) {
    if (item.section_id !== sectionId) continue;
    const match = String(item.external_id || "").match(
      new RegExp(`^${prefix}(\\d+)$`, "i"),
    );
    if (match) maxNum = Math.max(maxNum, parseInt(match[1], 10));
  }
  return `${prefix}${String(maxNum + 1).padStart(2, "0")}`;
}

const Questionnaire = lazy(() =>
  import("./Questionnaire").then((mod) => ({ default: mod.Questionnaire })),
);

const FORM_KEYS = ["gap_assessment", "ropa", "pia", "tprm"];

const FORM_KEY_MODULE = {
  gap_assessment: "gap",
  ropa: "ropa",
  pia: "dpia",
  tprm: "tprm",
};

async function parseApiError(res, fallback = "Request failed") {
  const data = await res.json().catch(() => ({}));
  const detail = data.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => d.msg || d.message || JSON.stringify(d))
      .join("; ");
  }
  return data.message || fallback;
}

const FORM_META = {
  gap_assessment: {
    label: "Gap Assessment",
    icon: ClipboardCheck,
    gradient: "from-indigo-500 to-violet-600",
    tabIconColor: "text-indigo-500",
  },
  ropa: {
    label: "ROPA",
    icon: FileText,
    gradient: "from-emerald-500 to-teal-600",
    tabIconColor: "text-emerald-500",
  },
  pia: {
    label: "PIA",
    icon: Shield,
    gradient: "from-violet-500 to-purple-600",
    tabIconColor: "text-violet-500",
  },
  tprm: {
    label: "TPRM",
    icon: ClipboardList,
    gradient: "from-cyan-500 to-blue-700",
    tabIconColor: "text-cyan-500",
    isTprm: true,
  },
};

const GAP_FIELD_TYPES = [
  { value: "gap_choice", label: "Yes / Partial / No / N/A" },
  { value: "text", label: "Short Text" },
  { value: "textarea", label: "Long Text" },
  { value: "boolean", label: "Yes / No" },
  { value: "select", label: "Single Choice" },
  { value: "multiselect", label: "Multi Choice" },
  { value: "checkbox_single", label: "Single Choice (Checkbox)" },
  { value: "checkbox_multi", label: "Multi Choice (Checkbox)" },
  { value: "rating", label: "Rating" },
  { value: "file", label: "File Upload" },
];

const FIELD_TYPES = [
  { value: "text", label: "Short Text" },
  { value: "textarea", label: "Long Text" },
  { value: "select", label: "Single Choice" },
  { value: "multiselect", label: "Multi Choice" },
  { value: "checkbox_single", label: "Single Choice (Checkbox)" },
  { value: "checkbox_multi", label: "Multi Choice (Checkbox)" },
  { value: "toggle", label: "Toggle" },
  { value: "date", label: "Date" },
  { value: "boolean", label: "Yes/No" },
  { value: "rating", label: "Rating" },
  { value: "gap_choice", label: "Gap Choice (Y/P/N/N/A)" },
];

const BLANK = {
  section_id: "general",
  external_id: "",
  text: "",
  description: "",
  field_type: "text",
  required: true,
  options: [],
  config: {},
  is_active: true,
  order: "",
  weight: 1,
  allow_attachment: false,
  attachment_required: false,
};

// Fixed answer set for Gap Assessment ("gap_choice") questions. The choices
// themselves can't be added/removed — the form always shows Yes/Partially/
// No/N/A — but each question can assign its own point value per answer.
// These are the historical fixed point values, used as sensible starting
// defaults so existing questions don't silently change score when a tenant
// first opens them for editing.
const GAP_CHOICE_DEFAULT_WEIGHTS = { Yes: 1, Partially: 0.5, No: 0, "N/A": 0 };
const GAP_CHOICE_LABELS = ["Yes", "Partially", "No", "N/A"];

const gapChoiceOptionsWithDefaults = (opts = []) => {
  const byLabel = {};
  (opts || []).forEach((o) => {
    if (typeof o === "string") byLabel[o] = GAP_CHOICE_DEFAULT_WEIGHTS[o] ?? 1;
    else if (o && o.label)
      byLabel[o.label] = Number(
        o.weight ?? GAP_CHOICE_DEFAULT_WEIGHTS[o.label] ?? 1,
      );
  });
  return GAP_CHOICE_LABELS.map((label) => ({
    label,
    weight:
      label in byLabel ? byLabel[label] : GAP_CHOICE_DEFAULT_WEIGHTS[label],
  }));
};

const SCORABLE_OPTION_FIELD_TYPES = new Set([
  "select",
  "multiselect",
  "checkbox_single",
  "checkbox_multi",
  "toggle",
  "boolean",
  "rating",
  "gap_choice",
]);

const normalizeOptions = (opts = [], fieldType) => {
  if (fieldType === "gap_choice") {
    return gapChoiceOptionsWithDefaults(opts).map((o) => ({
      label: o.label,
      weight: o.label === "N/A" ? 0 : Number(o.weight) || 0,
    }));
  }
  if (SCORABLE_OPTION_FIELD_TYPES.has(fieldType)) {
    return (opts || [])
      .map((o) => {
        if (typeof o === "string") return { label: o, weight: 1 };
        return {
          label: o.label || o.value || "",
          weight: Number(o.weight ?? o.score ?? 1) || 0,
        };
      })
      .filter((o) => o.label);
  }
  return opts.map((o) =>
    typeof o === "string" ? o : o.label || o.value || "",
  );
};

const REFERENCE_EVIDENCE_FORM_KEYS = new Set(["gap_assessment", "pia"]);

function ReferenceEvidenceAdminPanel({ formKey, questionId }) {
  const { showNotification } = useApp();
  const [evidence, setEvidence] = useState(null);
  const [loading, setLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const load = useCallback(async () => {
    if (!questionId || !REFERENCE_EVIDENCE_FORM_KEYS.has(formKey)) return;
    setLoading(true);
    try {
      const res = await apiFetch(
        `/tenant/form-questions/${formKey}/${questionId}/reference-evidence`,
      );
      if (res.ok) setEvidence(await res.json());
    } catch {
      /* non-fatal */
    } finally {
      setLoading(false);
    }
  }, [formKey, questionId]);

  useEffect(() => {
    load();
    if (!questionId) return undefined;
    const timer = setInterval(load, 6000);
    return () => clearInterval(timer);
  }, [load, questionId]);

  const regenerate = async () => {
    if (!questionId) return;
    setRegenerating(true);
    try {
      const res = await apiFetch(
        `/tenant/form-questions/${formKey}/${questionId}/reference-evidence/regenerate`,
        { method: "POST" },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Regeneration failed");
      setEvidence(data);
      showNotification("Stored evidence refresh started", "info");
    } catch (e) {
      showNotification(e.message || "Regeneration failed", "error");
    } finally {
      setRegenerating(false);
    }
  };

  if (!REFERENCE_EVIDENCE_FORM_KEYS.has(formKey)) return null;

  return (
    <ReferenceEvidenceAdminCard
      evidence={evidence}
      loading={loading}
      regenerating={regenerating}
      onRegenerate={questionId ? regenerate : null}
      onPreview={
        evidence?.has_file
          ? () => openAuthenticatedAttachment(referenceEvidencePreviewPath(evidence))
          : null
      }
      audienceLabel="users"
    />
  );
}

function selectedGapPolicyKeys(form) {
  const cfg = form?.config || {};
  if (cfg.policy_keys_locked) return cfg.policy_keys || [];
  const automatic = getPolicyIdsForQuestion(form?.section_id, form?.external_id);
  if (automatic.length) return automatic;
  return cfg.policy_keys || [];
}

function GapPolicyLinkFields({ form, setForm, storedPolicies }) {
  const catalogue = getDpdpPolicies();
  const selectedKeys = selectedGapPolicyKeys(form);
  const selectedDocs = form?.config?.policy_document_ids || [];
  const unlinkedPolicies = storedPolicies.filter((policy) => !policy.catalogue_key);

  const lockConfig = (policyKeys, documentIds) => {
    setForm({
      ...form,
      config: {
        ...(form.config || {}),
        policy_keys_locked: true,
        policy_keys: policyKeys,
        policy_document_ids: documentIds,
      },
    });
  };

  const toggleKey = (id) => {
    const next = new Set(selectedKeys);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    lockConfig([...next], selectedDocs);
  };

  const toggleDoc = (id) => {
    const next = new Set(selectedDocs);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    lockConfig(selectedKeys, [...next]);
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-3">
      <div>
        <p className="text-sm font-semibold text-slate-800">Policies for this gap</p>
        <p className="text-xs text-slate-500 mt-0.5">
          A No or Partially answer recommends these policies from Policy Store.
          Leave this untouched to keep the section default.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {catalogue.map((policy) => (
          <label
            key={policy.id}
            className="flex items-start gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs text-slate-700 cursor-pointer"
          >
            <input
              type="checkbox"
              className="mt-0.5 accent-indigo-600"
              checked={selectedKeys.includes(policy.id)}
              onChange={() => toggleKey(policy.id)}
            />
            <span>
              <span className="font-semibold block">{policy.title}</span>
              <span className="text-slate-400">{policy.actRef}</span>
            </span>
          </label>
        ))}
      </div>
      {unlinkedPolicies.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-slate-600">
            Organisation files that do not match a sample
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {unlinkedPolicies.map((policy) => (
                <label
                  key={policy.id}
                  className="flex items-start gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs text-slate-700 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    className="mt-0.5 accent-teal-600"
                    checked={selectedDocs.includes(policy.id)}
                    onChange={() => toggleDoc(policy.id)}
                  />
                  <span className="font-semibold">{policy.title}</span>
                </label>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StandardFormPanel({ formKey, onRegisterActions }) {
  const { showNotification } = useApp();
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({
    using_defaults: true,
    default_count: 0,
    active_custom_count: 0,
    total: 0,
    active_count: 0,
    inactive_count: 0,
    required_count: 0,
    sections_count: 0,
  });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [requiredFilter, setRequiredFilter] = useState("all");
  const [activeStatCard, setActiveStatCard] = useState("total");
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const PAGE_SIZE = 10;
  const [modal, setModal] = useState(false);
  const [linkedPolicies, setLinkedPolicies] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [optInput, setOptInput] = useState("");
  const [optWeight, setOptWeight] = useState(1);
  const [saving, setSaving] = useState(false);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [reorderingId, setReorderingId] = useState(null);

  const [viewQuestion, setViewQuestion] = useState(null);
  const [viewDescription, setViewDescription] = useState(null);
  const [activityQ, setActivityQ] = useState(null);
  const [evidenceViewRow, setEvidenceViewRow] = useState(null);
  const [evidenceCatalogMap, setEvidenceCatalogMap] = useState({});
  const [evidenceCatalogLoading, setEvidenceCatalogLoading] = useState(false);
  const [regeneratingEvidenceId, setRegeneratingEvidenceId] = useState(null);

  const formMeta = FORM_META[formKey];
  const label = formMeta?.label || formKey;
  const showEvidenceRows = REFERENCE_EVIDENCE_FORM_KEYS.has(formKey);
  const types = formKey === "gap_assessment" ? GAP_FIELD_TYPES : FIELD_TYPES;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(PAGE_SIZE),
      });
      if (debouncedSearch.trim()) params.set("search", debouncedSearch.trim());
      if (catFilter !== "all") params.set("section_id", catFilter);
      if (statusFilter !== "all") {
        params.set("is_active", String(statusFilter === "active"));
      }
      if (requiredFilter !== "all") {
        params.set("required", String(requiredFilter === "required"));
      }
      const res = await apiFetch(`/tenant/form-questions/${formKey}?${params}`);
      if (!res.ok) throw new Error((await res.json()).detail || "Failed");
      const data = await res.json();
      setItems(data.items || []);
      setPages(data.pages || 1);
      setMeta(data);
    } catch (e) {
      showNotification(e.message || "Failed to load questions", "error");
    } finally {
      setLoading(false);
      setLastRefreshed(new Date());
    }
  }, [
    formKey,
    page,
    debouncedSearch,
    catFilter,
    statusFilter,
    requiredFilter,
    showNotification,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  const loadEvidenceCatalog = useCallback(async () => {
    if (!showEvidenceRows) return;
    setEvidenceCatalogLoading(true);
    try {
      const res = await apiFetch(
        `/tenant/form-questions/${formKey}/reference-evidence/catalog`,
      );
      if (res.ok) {
        const data = await res.json();
        setEvidenceCatalogMap(buildEvidenceCatalogMap(data.items || []));
      }
    } catch {
      /* non-fatal */
    } finally {
      setEvidenceCatalogLoading(false);
    }
  }, [formKey, showEvidenceRows]);

  useEffect(() => {
    loadEvidenceCatalog();
  }, [loadEvidenceCatalog]);

  useEffect(() => {
    const hasPending = Object.values(evidenceCatalogMap).some(
      (item) => item && ["pending", "generating"].includes(item.generation_status),
    );
    if (!hasPending) return undefined;
    const timer = setInterval(loadEvidenceCatalog, 8000);
    return () => clearInterval(timer);
  }, [evidenceCatalogMap, loadEvidenceCatalog]);

  const regenerateEvidence = async (catalogItem) => {
    if (!catalogItem?.question_uuid) {
      showNotification(
        "Import defaults to generate evidence for platform default questions.",
        "info",
      );
      return;
    }
    setRegeneratingEvidenceId(catalogItem.question_uuid);
    try {
      const res = await apiFetch(
        `/tenant/form-questions/${formKey}/${catalogItem.question_uuid}/reference-evidence/regenerate`,
        { method: "POST" },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Regeneration failed");
      showNotification("Stored evidence refresh started", "info");
      await loadEvidenceCatalog();
    } catch (e) {
      showNotification(e.message || "Regeneration failed", "error");
    } finally {
      setRegeneratingEvidenceId(null);
    }
  };

  const handleCatChange = (val) => {
    setCatFilter(val);
    if (val !== "all") {
      setActiveStatCard("groups");
    } else {
      setActiveStatCard("total");
    }
  };

  const handleStatusChange = (val) => {
    setStatusFilter(val);
    if (val === "active") {
      setActiveStatCard("active");
    } else if (val === "inactive") {
      setActiveStatCard("inactive");
    } else if (requiredFilter === "required") {
      setActiveStatCard("required");
    } else {
      setActiveStatCard("total");
    }
  };

  const handleRequiredChange = (val) => {
    setRequiredFilter(val);
    if (val === "required") {
      setActiveStatCard("required");
    } else if (statusFilter === "active") {
      setActiveStatCard("active");
    } else if (statusFilter === "inactive") {
      setActiveStatCard("inactive");
    } else {
      setActiveStatCard("total");
    }
  };

  const handleCardClick = (key) => {
    setActiveStatCard(key);
    if (key === "total") {
      setCatFilter("all");
      setStatusFilter("all");
      setRequiredFilter("all");
    } else if (key === "groups") {
      setCatFilter("all");
      setStatusFilter("all");
      setRequiredFilter("all");
      const el = document.getElementById("section-select-dropdown");
      if (el) {
        el.focus();
      }
    } else if (key === "active") {
      setCatFilter("all");
      setStatusFilter("active");
      setRequiredFilter("all");
    } else if (key === "inactive") {
      setCatFilter("all");
      setStatusFilter("inactive");
      setRequiredFilter("all");
    } else if (key === "required") {
      setCatFilter("all");
      setStatusFilter("all");
      setRequiredFilter("required");
    }
  };

  useEffect(() => {
    setCatFilter("all");
    setStatusFilter("all");
    setRequiredFilter("all");
    setActiveStatCard("total");
    setPage(1);
  }, [formKey]);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [catFilter, statusFilter, requiredFilter]);

  useEffect(() => {
    if (!modal || formKey !== "gap_assessment") return undefined;
    let cancelled = false;
    apiFetch("/policy-store?page=1&page_size=100")
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled) setLinkedPolicies(data?.items || []);
      })
      .catch(() => {
        if (!cancelled) setLinkedPolicies([]);
      });
    return () => {
      cancelled = true;
    };
  }, [modal, formKey]);

  const openCreate = useCallback(() => {
    setEditing(null);
    const firstSection = FORM_SECTIONS[formKey]?.[0]?.id || "general";
    const fieldType = formKey === "gap_assessment" ? "gap_choice" : "text";
    setForm({
      ...BLANK,
      section_id: firstSection,
      field_type: fieldType,
      options:
        fieldType === "gap_choice" ? gapChoiceOptionsWithDefaults([]) : [],
    });

    setOptInput("");
    setOptWeight(1);
    setModal(true);
  }, [formKey]);

  const ensureCustomized = async (currentRow) => {
    if (!meta.using_defaults) return currentRow;
    const importRes = await apiFetch(
      `/tenant/form-questions/${formKey}/import-defaults?queue_evidence=false`,
      {
        method: "POST",
      },
    );
    if (!importRes.ok) {
      throw new Error(
        "Failed to auto-import defaults. Please try clicking 'Import Defaults' manually.",
      );
    }
    // Fetch the customized list immediately
    const params = new URLSearchParams({
      page: "1",
      page_size: "200",
    });
    const res = await apiFetch(`/tenant/form-questions/${formKey}?${params}`);
    if (!res.ok)
      throw new Error("Failed to reload questions after auto-import.");
    const data = await res.json();

    const matchingRow = (data.items || []).find(
      (item) =>
        item.section_id === currentRow.section_id &&
        item.external_id === currentRow.external_id,
    );
    if (!matchingRow)
      throw new Error("Could not locate the imported question.");

    setItems(data.items || []);
    setMeta(data);
    setPages(data.pages || 1);

    return matchingRow;
  };

  const openEdit = async (row) => {
    try {
      let targetRow = row;
      if (meta.using_defaults) {
        showNotification("Auto-importing default questions first...", "info");
        targetRow = await ensureCustomized(row);
      }
      setEditing(targetRow);
      setForm({
        section_id: targetRow.section_id,
        external_id: targetRow.external_id,
        text: targetRow.text,
        description: targetRow.description || "",
        field_type: targetRow.field_type,
        required: targetRow.required,
        options:
          targetRow.field_type === "gap_choice"
            ? gapChoiceOptionsWithDefaults(targetRow.options)
            : (targetRow.options || []).map((o) =>
                typeof o === "string" ? { label: o, weight: 1 } : o,
              ),
        config: targetRow.config || {},
        is_active: targetRow.is_active,
        order: targetRow.order >= 1 ? targetRow.order : "",
        weight: targetRow.weight ?? 1,
        allow_attachment: targetRow.allow_attachment ?? false,
        attachment_required: targetRow.attachment_required ?? false,
      });
      setOptInput("");
      setModal(true);
    } catch (e) {
      showNotification(e.message, "error");
    }
  };

  const parseSortOrder = (val, fallback) => {
    const n = Number(val);
    if (Number.isFinite(n) && n >= 1) return n;
    if (Number.isFinite(Number(fallback)) && Number(fallback) >= 1) {
      return Number(fallback);
    }
    return undefined;
  };

  const save = async () => {
    if (!form.text.trim() || !form.section_id.trim()) {
      showNotification("Section and question text are required", "error");
      return;
    }
    setSaving(true);
    try {
      let targetEditing = editing;
      if (editing && meta.using_defaults) {
        showNotification("Auto-importing default questions first...", "info");
        targetEditing = await ensureCustomized(editing);
        setEditing(targetEditing);
      }

      let autoId;
      if (formKey === "pia" || formKey === "ropa") {
        const codes = {
          // PIA
          general: "GEN",
          dataDetails: "DAT",
          dataFlow: "FLW",
          risk: "RSK",
          vendorControls: "VND",
          safeguards: "SAF",
          // ROPA
          org: "ORG",
          activity: "ACT",
          legal: "LEG",
          principals: "PRN",
          personaldata: "DAT",
          lifecycle: "LFC",
          sharing: "SHR",
          retention: "RET",
          security: "SEC",
          rights: "RGT",
          incident: "INC",
          approval: "APP",
        };
        const secCode = codes[form.section_id] || "GEN";
        const sectionItems = items.filter(
          (item) => item.section_id === form.section_id,
        );
        const nextNum = sectionItems.length + 1;
        autoId = `${secCode}${String(nextNum).padStart(2, "0")}`;
      } else if (formKey === "gap_assessment") {
        autoId = nextGapQuestionId(form.section_id, items);
      } else {
        autoId =
          form.section_id.toUpperCase().slice(0, 4) +
          "_" +
          Date.now().toString(36).toUpperCase();
      }
      const { external_id, order: _ignoredOrder, ...formRest } = form;
      const bodyForCreate = { ...formRest, external_id: autoId };

      const weightVal = Number(form.weight) || 1;
      const normalizedOptions = normalizeOptions(form.options, form.field_type);
      const order = parseSortOrder(
        form.order,
        editing?.order ?? items.length + 1,
      );

      const body = targetEditing
        ? {
            section_id: form.section_id,
            external_id: form.external_id,
            text: form.text,
            description: form.description,
            field_type: form.field_type,
            required: form.required,
            options: normalizedOptions,
            config: form.config,
            is_active: form.is_active,
            allow_attachment: form.allow_attachment,
            attachment_required: form.attachment_required,
            weight: weightVal,
            ...(order !== undefined ? { order } : {}),
          }
        : {
            ...bodyForCreate,
            weight: weightVal,
            options: normalizedOptions,
            ...(order !== undefined ? { order } : {}),
          };

      const res = targetEditing
        ? await apiFetch(`/tenant/form-questions/${formKey}/${targetEditing.id}`, {
            method: "PATCH",
            body: JSON.stringify(body),
          })
        : await apiFetch(`/tenant/form-questions/${formKey}`, {
            method: "POST",
            body: JSON.stringify(body),
          });
      if (!res.ok) throw new Error((await res.json()).detail || "Save failed");
      showNotification(
        targetEditing ? "Question updated" : "Question added",
        "success",
      );
      setModal(false);
      const deactivatedWhileActiveFilter =
        Boolean(targetEditing) &&
        form.is_active === false &&
        statusFilter === "active";
      if (deactivatedWhileActiveFilter) {
        setStatusFilter("all");
        setActiveStatCard("total");
      } else {
        await load();
      }
      await loadEvidenceCatalog();
    } catch (e) {
      showNotification(e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const showReorderColumn =
    !meta.using_defaults &&
    !debouncedSearch.trim() &&
    statusFilter === "all" &&
    requiredFilter === "all";

  const canDragQuestions = showReorderColumn && catFilter !== "all";

  const getReorderGripTitle = () => {
    if (!showReorderColumn) return "";
    if (catFilter === "all") {
      return "Select a category to reorder questions within that section";
    }
    if (debouncedSearch.trim()) {
      return "Clear search to reorder questions";
    }
    if (statusFilter !== "all" || requiredFilter !== "all") {
      return "Clear filters to reorder questions";
    }
    return "Drag to reorder";
  };

  const getSerialNumber = (row, idx) => {
    if (catFilter !== "all") {
      return row.order >= 1
        ? row.order
        : (page - 1) * PAGE_SIZE + idx + 1;
    }
    return (page - 1) * PAGE_SIZE + idx + 1;
  };

  const reorderQuestion = async (draggedId, dropIndex, fromIndex) => {
    if (catFilter === "all") {
      showNotification(
        "Select a category before reordering questions.",
        "warning",
      );
      return;
    }
    if (!canDragQuestions) {
      if (debouncedSearch.trim()) {
        showNotification("Clear search to reorder questions", "warning");
      } else if (statusFilter !== "all" || requiredFilter !== "all") {
        showNotification("Clear filters to reorder questions", "warning");
      }
      return;
    }
    if (fromIndex === dropIndex) return;

    const draggedRow = filtered[fromIndex];
    const dropRow = filtered[dropIndex];
    if (!draggedRow || !dropRow) return;

    if (draggedRow.section_id !== dropRow.section_id) {
      showNotification(
        "Questions can only be reordered within the same section.",
        "warning",
      );
      return;
    }

    const sectionId = draggedRow.section_id;
    const sectionOnPage = filtered.filter(
      (r) => r.section_id === sectionId,
    );
    const dropSectionIndex = sectionOnPage.findIndex(
      (r) => r.id === dropRow.id,
    );
    const newOrder = (page - 1) * PAGE_SIZE + dropSectionIndex + 1;

    const prevItems = items;
    const reordered = [...filtered];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(dropIndex, 0, moved);
    setItems(reordered);
    setReorderingId(draggedId);

    try {
      let targetId = draggedId;
      if (meta.using_defaults) {
        showNotification("Auto-importing default questions first...", "info");
        const imported = await ensureCustomized(draggedRow);
        targetId = imported.id;
      }
      const params = new URLSearchParams({
        order: String(newOrder),
        section_id: sectionId,
      });
      const res = await apiFetch(
        `/tenant/form-questions/${formKey}/${targetId}/reorder?${params}`,
        { method: "POST" },
      );
      if (!res.ok) throw new Error(await parseApiError(res, "Could not reorder"));
      await load();
    } catch (e) {
      setItems(prevItems);
      showNotification(e.message || "Could not reorder", "error");
    } finally {
      setReorderingId(null);
    }
  };

  const handleReorderDrop = (dropIndex) => {
    if (!draggingId) return;
    const fromIndex = filtered.findIndex((r) => r.id === draggingId);
    const draggedId = draggingId;
    setDraggingId(null);
    setDragOverIndex(null);
    if (fromIndex === -1) return;
    reorderQuestion(draggedId, dropIndex, fromIndex);
  };

  const remove = async (row) => {
    const ok = await confirmAction({
      title: "Delete question?",
      text: "This question will be permanently removed from your form.",
      confirmText: "Delete",
      icon: "warning",
    });
    if (!ok) return;
    try {
      let targetRow = row;
      if (meta.using_defaults) {
        showNotification("Auto-importing default questions first...", "info");
        targetRow = await ensureCustomized(row);
      }
      const displayId = getDisplayId(row);
      const res = await apiFetch(
        `/tenant/form-questions/${formKey}/${targetRow.id}?display_id=${encodeURIComponent(displayId)}`,
        { method: "DELETE" },
      );
      if (!res.ok)
        throw new Error((await res.json()).detail || "Delete failed");
      showNotification("Question deleted", "success");
      load();
    } catch (e) {
      showNotification(e.message, "error");
    }
  };

  const getDisplayId = (row) => {
    if (formKey === "gap_assessment") return row.external_id;
    if (/^[A-Z]{3,4}\d+$/.test(row.external_id)) {
      return row.external_id;
    }
    const codes = {
      // PIA
      general: "GEN",
      dataDetails: "DAT",
      dataFlow: "FLW",
      risk: "RSK",
      vendorControls: "VND",
      safeguards: "SAF",
      // ROPA
      org: "ORG",
      activity: "ACT",
      legal: "LEG",
      principals: "PRN",
      personaldata: "DAT",
      lifecycle: "LFC",
      sharing: "SHR",
      retention: "RET",
      security: "SEC",
      rights: "RGT",
      incident: "INC",
      approval: "APP",
    };
    const secCode = codes[row.section_id] || "GEN";
    if (row.order >= 1) {
      return `${secCode}${String(row.order).padStart(2, "0")}`;
    }
    const sectionItems = items
      .filter((item) => item.section_id === row.section_id)
      .sort((a, b) => (a.order || 0) - (b.order || 0));
    const idx = sectionItems.findIndex(
      (item) => item.id === row.id || item.external_id === row.external_id,
    );
    const num = idx !== -1 ? idx + 1 : 1;
    return `${secCode}${String(num).padStart(2, "0")}`;
  };

  const filtered = items;
  const evidenceViewItem = evidenceViewRow
    ? lookupEvidenceCatalogItem(evidenceViewRow, evidenceCatalogMap)
    : null;

  useEffect(() => {
    if (!onRegisterActions) return undefined;
    onRegisterActions({
      openCreate,
      refresh: async () => {
        await load();
        await loadEvidenceCatalog();
      },
    });
  }, [onRegisterActions, openCreate, load, loadEvidenceCatalog]);

  return (
    <>
      <FormQuestionsStatGrid
        meta={meta}
        loading={loading}
        groupLabel="Sections"
        onCardClick={handleCardClick}
        activeKey={activeStatCard}
      />

      <Card>
        {meta.using_defaults && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg text-sm">
            <strong>Platform defaults in use.</strong> Click{" "}
            <strong>"Import Defaults"</strong> at the top right to customize,
            edit, or delete these questions.
          </div>
        )}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 max-w-sm">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all"
              placeholder={`Search ${label} questions…`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <select
            id="section-select-dropdown"
            value={catFilter}
            onChange={(e) => handleCatChange(e.target.value)}
            className="text-sm border border-slate-200 rounded-xl px-3 py-2 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all cursor-pointer"
          >
            <option value="all">All Categories</option>
            {(FORM_SECTIONS[formKey] || []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={(e) => handleStatusChange(e.target.value)}
            className="text-sm border border-slate-200 rounded-xl px-3 py-2 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all cursor-pointer"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          <select
            value={requiredFilter}
            onChange={(e) => handleRequiredChange(e.target.value)}
            className="text-sm border border-slate-200 rounded-xl px-3 py-2 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all cursor-pointer"
          >
            <option value="all">All Questions</option>
            <option value="required">Required</option>
            <option value="optional">Optional</option>
          </select>

          {["pia", "ropa"].includes(formKey) && (
            <ScoringLevelsPanel formKey={formKey} inline />
          )}

          {/* Refresh */}
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              disabled={loading}
              className="p-2 rounded-xl text-slate-400 hover:text-blue-600 hover:bg-blue-50 border border-slate-200 transition-all disabled:opacity-40"
              title="Refresh questions list"
            >
              <RotateCw size={14} className={loading ? "animate-spin" : ""} />
            </button>
            {lastRefreshed && (
              <span className="text-xs text-slate-400">
                Last refreshed {lastRefreshed.toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>

        {loading ? (
          <p className="text-slate-500 text-sm py-8 text-center">Loading…</p>
        ) : meta.using_defaults && filtered.length === 0 ? (
          <EmptyState
            title="Platform defaults in use"
            description={`Users see ${meta.default_count} built-in questions. Import defaults to edit them, or add your own.`}
          />
        ) : filtered.length === 0 ? (
          <EmptyState title="No questions" />
        ) : (
          <>
            <div className="w-full max-w-full overflow-auto scrollbar-thin max-h-[min(560px,calc(100dvh-17rem))] rounded-lg border border-slate-100">
              <table
                className={`w-max min-w-full text-sm table-auto ${
                  formKey === "gap_assessment"
                    ? showEvidenceRows ? "min-w-[1040px]" : "min-w-[960px]"
                    : showEvidenceRows ? "min-w-[1360px]" : "min-w-[1280px]"
                }`}
              >
                <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_rgb(226,232,240)]">
                <tr className="text-left text-slate-500 border-b">
                  {showReorderColumn && <th className="pb-2 pr-2 w-8" />}
                  <th className="pb-2 pr-3 whitespace-nowrap min-w-[3rem]">S.No.</th>
                  <th className="pb-2 pr-3 whitespace-nowrap min-w-[5rem]">ID</th>
                  <th className="pb-2 pr-3 whitespace-nowrap min-w-[8rem]">Section</th>
                  <th className="pb-2 pr-3 whitespace-nowrap min-w-[16rem]">Question</th>
                  {formKey !== "gap_assessment" && (
                    <th className="pb-2 pr-3 whitespace-nowrap min-w-[12rem]">Description</th>
                  )}

                  <th className="pb-2 pr-3 whitespace-nowrap min-w-[4rem]">Weight</th>
                  <th className="pb-2 pr-3 whitespace-nowrap min-w-[7rem]">Type</th>
                  <th className="pb-2 pr-3 whitespace-nowrap min-w-[5rem]">Status</th>
                  {showEvidenceRows && (
                    <th className="pb-2 pr-3 whitespace-nowrap min-w-[9rem]">{STORED_EVIDENCE_TABLE_COLUMN}</th>
                  )}
                  <th className="pb-2 whitespace-nowrap min-w-[5.5rem]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row, idx) => {
                  const catalogItem = lookupEvidenceCatalogItem(row, evidenceCatalogMap);

                  return (
                  <tr
                    key={row.id}
                    onDragOver={(e) => {
                      if (!draggingId || draggingId === row.id) return;
                      const dragged = filtered.find((r) => r.id === draggingId);
                      if (
                        dragged &&
                        dragged.section_id !== row.section_id
                      ) {
                        e.dataTransfer.dropEffect = "none";
                        return;
                      }
                      e.preventDefault();
                      setDragOverIndex(idx);
                    }}
                    onDragLeave={() => {
                      if (dragOverIndex === idx) setDragOverIndex(null);
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      handleReorderDrop(idx);
                    }}
                    className={`border-b border-slate-50 transition-colors hover:bg-slate-50/40 ${
                      draggingId === row.id ? "opacity-50" : ""
                    } ${
                      dragOverIndex === idx && draggingId !== row.id
                        ? "bg-indigo-50 ring-1 ring-inset ring-indigo-200"
                        : ""
                    } ${reorderingId === row.id ? "pointer-events-none" : ""}`}
                  >
                    {showReorderColumn && (
                      <td className="py-2 pr-2 align-middle w-8" onClick={(e) => e.stopPropagation()}>
                        <span
                          draggable={canDragQuestions}
                          onDragStart={(e) => {
                            if (!canDragQuestions) {
                              e.preventDefault();
                              return;
                            }
                            e.dataTransfer.setData("text/plain", row.id);
                            e.dataTransfer.effectAllowed = "move";
                            setDraggingId(row.id);
                          }}
                          onDragEnd={() => {
                            setDraggingId(null);
                            setDragOverIndex(null);
                          }}
                          className={`inline-flex p-1 rounded text-slate-400 ${
                            canDragQuestions
                              ? "hover:text-slate-600 hover:bg-slate-100 cursor-grab active:cursor-grabbing"
                              : "opacity-30 cursor-not-allowed"
                          }`}
                          title={getReorderGripTitle()}
                        >
                          <GripVertical size={14} />
                        </span>
                      </td>
                    )}
                    <td className="py-2 pr-3 align-middle text-slate-500 font-medium">
                      {getSerialNumber(row, idx)}
                    </td>
                    <td className="py-2 pr-3 align-middle" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => setActivityQ(row)}
                        className="px-2.5 py-1 text-[11px] font-semibold font-mono rounded-lg border border-slate-200 bg-slate-50 text-slate-600 hover:bg-indigo-50 hover:text-indigo-600 hover:border-indigo-200 transition-all cursor-pointer shadow-sm hover:scale-[1.02]"
                        title="Click to view activity history logs"
                      >
                        {getDisplayId(row)}
                      </button>
                    </td>
                    <td className="py-2 pr-3 text-slate-600 align-middle">
                      {FORM_SECTIONS[formKey]?.find(
                        (s) => s.id === row.section_id,
                      )?.label || row.section_id}
                    </td>
                    <td className="py-2 pr-3 align-middle min-w-[16rem] max-w-[28rem]" onClick={(e) => e.stopPropagation()}>
                      <div
                        className="truncate cursor-pointer hover:text-indigo-600 hover:underline transition-colors"
                        onClick={() => setViewQuestion(row.text)}
                        title="Click to view full question"
                      >
                        {row.text}
                      </div>
                    </td>
                    {formKey !== "gap_assessment" && (
                      <td className="py-2 pr-3 align-middle min-w-[12rem] max-w-[22rem]" onClick={(e) => e.stopPropagation()}>
                        {row.description ? (
                          <div
                            className="truncate text-slate-500 cursor-pointer hover:text-indigo-600 hover:underline transition-colors"
                            onClick={() => setViewDescription(row.description)}
                            title="Click to view full description"
                          >
                            {row.description}
                          </div>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                    )}

                    <td className="py-2 pr-3 align-middle font-medium text-slate-600">
                      {row.weight ?? 1}
                    </td>
                    <td className="py-2 pr-3 align-middle">{row.field_type}</td>
                    <td className="py-2 pr-3 align-middle">
                      <Badge variant={row.is_active ? "success" : "inactive"}>
                        {row.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </td>
                    {showEvidenceRows && (
                      <td className="py-2 pr-3 align-middle min-w-[9rem]">
                        <ReferenceEvidenceTableCell
                          catalogItem={catalogItem}
                          loading={evidenceCatalogLoading && !catalogItem}
                          onView={() => setEvidenceViewRow(row)}
                        />
                      </td>
                    )}
                    <td className="py-2 align-middle" onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-1 items-center">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEdit(row)}
                          aria-label="Edit"
                        >
                          <Edit2 size={14} className="text-indigo-600" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => remove(row)}
                          aria-label="Delete"
                          disabled={row.is_active}
                          className="disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                          title={
                            row.is_active
                              ? "Active questions cannot be deleted"
                              : "Delete"
                          }
                        >
                          <Trash2
                            size={14}
                            className={
                              row.is_active ? "text-slate-400" : "text-red-500"
                            }
                          />
                        </Button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
            <Pagination
              page={page}
              pages={pages}
              total={meta.total || 0}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
              itemLabel="questions"
              className="mt-4"
            />
          </>
        )}
      </Card>

      <Modal
        open={modal}
        onClose={() => setModal(false)}
        title={editing ? `Edit ${label} Question` : `Add ${label} Question`}
        size="lg"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Select
                label="Section"
                value={form.section_id}
                onChange={(e) =>
                  setForm({ ...form, section_id: e.target.value })
                }
              >
                {(FORM_SECTIONS[formKey] || []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </Select>
            </div>

            <div>
              {/* Question ID is auto-generated by the backend — show read-only when editing */}
              {editing ? (
                <Input
                  label="Question ID"
                  value={getDisplayId(form)}
                  disabled
                  hint="Auto-generated by system"
                />
              ) : (
                <div className="text-xs text-slate-400 mt-6 font-medium">
                  Question ID auto-generated on create
                </div>
              )}
            </div>
          </div>
          {editing && form.config && Object.keys(form.config).length > 0 && (
            <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-500 space-y-1">
              <p className="font-semibold text-slate-600">
                Field context (from form)
              </p>
              {form.config.section_title && (
                <p>
                  Section:{" "}
                  <span className="text-slate-700">
                    {form.config.section_title}
                  </span>
                </p>
              )}
              {form.config.dpdp_ref && (
                <p>
                  DPDP ref:{" "}
                  <span className="text-slate-700">{form.config.dpdp_ref}</span>
                </p>
              )}
              {form.config.severity && (
                <p>
                  Severity:{" "}
                  <span className="text-slate-700">{form.config.severity}</span>
                </p>
              )}
            </div>
          )}
          <Input
            label="Question Text"
            value={form.text}
            onChange={(e) => setForm({ ...form, text: e.target.value })}
          />
          {formKey !== "gap_assessment" && (
            <Input
              label="Description"
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
            />
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Select
                label="Field Type"
                value={form.field_type}
                onChange={(e) => {
                  const ft = e.target.value;
                  const defaults = {
                    boolean: [
                      { label: "Yes", weight: 5 },
                      { label: "No", weight: 1 },
                    ],
                    rating: [
                      { label: "1", weight: 1 },
                      { label: "2", weight: 2 },
                      { label: "3", weight: 3 },
                      { label: "4", weight: 4 },
                      { label: "5", weight: 5 },
                    ],
                  };
                  const shouldInject =
                    defaults[ft] && form.options.length === 0;
                  setForm({
                    ...form,
                    field_type: ft,
                    options: shouldInject ? defaults[ft] : form.options,
                  });
                }}
              >
                {types.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </Select>
            </div>

            {formKey === "gap_assessment" ? (
              <div>
                <Input
                  label="DPDP Reference"
                  value={form.config?.dpdp_ref || ""}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      config: { ...form.config, dpdp_ref: e.target.value },
                    })
                  }
                />
              </div>
            ) : (
              <div className="flex items-center gap-6 mt-6">
                <label className="flex items-center gap-2 text-sm cursor-pointer font-medium text-slate-700">
                  <input
                    type="checkbox"
                    checked={form.required}
                    onChange={(e) =>
                      setForm({ ...form, required: e.target.checked })
                    }
                  />
                  Required
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer font-medium text-slate-700">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) =>
                      setForm({ ...form, is_active: e.target.checked })
                    }
                  />
                  Active
                </label>
              </div>
            )}
          </div>

          {formKey === "gap_assessment" && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Select
                  label="Severity"
                  value={form.config?.severity || "Medium"}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      config: { ...form.config, severity: e.target.value },
                    })
                  }
                >
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </Select>
              </div>

              <div className="flex items-center gap-6 mt-6">
                <label className="flex items-center gap-2 text-sm cursor-pointer font-medium text-slate-700">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 border-slate-300"
                    checked={form.required}
                    onChange={(e) =>
                      setForm({ ...form, required: e.target.checked })
                    }
                  />
                  Required
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer font-medium text-slate-700">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 border-slate-300"
                    checked={form.is_active}
                    onChange={(e) =>
                      setForm({ ...form, is_active: e.target.checked })
                    }
                  />
                  Active
                </label>
              </div>
            </div>
          )}
          {form.field_type === "gap_choice" && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Answer Point Values
                <span className="ml-2 text-xs font-normal text-slate-400">
                  Set how many points each answer is worth for this specific
                  question. The four answers themselves are fixed — only the
                  points change.
                </span>
              </label>
              <div className="space-y-1">
                {gapChoiceOptionsWithDefaults(form.options).map((o) => (
                  <div
                    key={o.label}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-100 bg-slate-50 text-sm"
                  >
                    <span className="flex-1 text-slate-700">{o.label}</span>
                    {o.label === "N/A" ? (
                      <span className="text-xs text-slate-400 italic mr-1">
                        always excluded from scoring
                      </span>
                    ) : (
                      <>
                        <span className="text-xs text-slate-400 mr-1">
                          points
                        </span>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.5"
                          className="w-20 border rounded px-2 py-0.5 text-xs text-center"
                          value={o.weight}
                          onChange={(e) => {
                            const val = Number(e.target.value);
                            const updated = gapChoiceOptionsWithDefaults(
                              form.options,
                            ).map((opt) =>
                              opt.label === o.label
                                ? { ...opt, weight: isNaN(val) ? 0 : val }
                                : opt,
                            );
                            setForm({ ...form, options: updated });
                          }}
                        />
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          {[
            "select",
            "multiselect",
            "checkbox_single",
            "checkbox_multi",
            "boolean",
            "rating",
            "text",
            "textarea",
          ].includes(form.field_type) && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {["boolean", "rating"].includes(form.field_type)
                  ? "Answer Weights"
                  : "Options & Weights"}
                <span className="ml-2 text-xs font-normal text-slate-400">
                  {["text", "textarea"].includes(form.field_type)
                    ? "Optional — add specific expected answers with weights"
                    : "Set a weight per answer to control its score contribution"}
                </span>
              </label>
              <div className="flex gap-2 mb-2">
                <input
                  className="flex-1 border rounded-lg px-3 py-2 text-sm"
                  value={optInput}
                  onChange={(e) => setOptInput(e.target.value)}
                  placeholder="Option label"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      const v = optInput.trim();
                      if (v && !form.options.find((o) => o.label === v)) {
                        setForm({
                          ...form,
                          options: [
                            ...form.options,
                            { label: v, weight: Number(optWeight) || 1 },
                          ],
                        });
                        setOptInput("");
                        setOptWeight(1);
                      }
                    }
                  }}
                />
                <input
                  type="number"
                  min="1"
                  max="100"
                  className="w-20 border rounded-lg px-3 py-2 text-sm text-center"
                  value={optWeight}
                  onChange={(e) => setOptWeight(e.target.value)}
                  title="Weight for this option"
                  placeholder="Wt"
                />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    const v = optInput.trim();
                    if (v && !form.options.find((o) => o.label === v)) {
                      setForm({
                        ...form,
                        options: [
                          ...form.options,
                          { label: v, weight: Number(optWeight) || 1 },
                        ],
                      });
                      setOptInput("");
                      setOptWeight(1);
                    }
                  }}
                >
                  Add
                </Button>
              </div>
              <div className="space-y-1">
                {form.options.map((o, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-100 bg-slate-50 text-sm"
                  >
                    <span className="flex-1 text-slate-700">{o.label}</span>
                    <span className="text-xs text-slate-400 mr-1">weight</span>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      className="w-14 border rounded px-2 py-0.5 text-xs text-center"
                      value={o.weight}
                      onChange={(e) => {
                        const updated = form.options.map((opt, i) =>
                          i === idx
                            ? { ...opt, weight: Number(e.target.value) || 1 }
                            : opt,
                        );
                        setForm({ ...form, options: updated });
                      }}
                    />
                    <button
                      type="button"
                      className="text-red-400 hover:text-red-600 ml-1 text-xs font-bold"
                      onClick={() =>
                        setForm({
                          ...form,
                          options: form.options.filter((_, i) => i !== idx),
                        })
                      }
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Input
                label="Question Weight"
                type="number"
                min="1"
                max="5"
                value={form.weight}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === "") {
                    setForm({ ...form, weight: "" });
                    return;
                  }
                  let num = Number(val);
                  if (isNaN(num)) return;
                  if (num > 5) num = 5;
                  if (num < 1) num = 1;
                  setForm({ ...form, weight: num });
                }}
                hint={
                  form.field_type === "gap_choice"
                    ? "How important this question is overall (multiplies its points)"
                    : [
                          "select",
                          "multiselect",
                          "checkbox_single",
                          "checkbox_multi",
                        ].includes(form.field_type)
                      ? "Fallback weight"
                      : "Affects scoring"
                }
              />
            </div>

            <div>
              <Input
                label="Sort Order"
                type="number"
                min="1"
                value={form.order}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === "") {
                    setForm({ ...form, order: "" });
                  } else {
                    setForm({
                      ...form,
                      order: Math.max(1, parseInt(val) || 1),
                    });
                  }
                }}
              />
            </div>
          </div>

          {/* Attachment toggles in a 2-column grid */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label
                className={`flex items-center justify-between px-3 py-2.5 rounded-xl border-2 cursor-pointer transition-all ${
                  form.allow_attachment
                    ? "border-blue-200 bg-blue-50 text-blue-700"
                    : "border-slate-100 text-slate-500 hover:border-slate-200"
                }`}
              >
                <span className="text-sm font-medium">Allow Attachment</span>
                <input
                  type="checkbox"
                  className="w-4 h-4 accent-blue-600"
                  checked={form.allow_attachment}
                  onChange={(e) => {
                    const val = e.target.checked;
                    setForm({
                      ...form,
                      allow_attachment: val,
                      attachment_required: val
                        ? form.attachment_required
                        : false,
                    });
                  }}
                />
              </label>
            </div>
            <div>
              <label
                className={`flex items-center justify-between px-3 py-2.5 rounded-xl border-2 cursor-pointer transition-all ${
                  !form.allow_attachment ? "opacity-40 cursor-not-allowed" : ""
                } ${
                  form.allow_attachment && form.attachment_required
                    ? "border-amber-200 bg-amber-50 text-amber-700"
                    : "border-slate-100 text-slate-500 hover:border-slate-200"
                }`}
              >
                <span className="text-sm font-medium">Attachment Required</span>
                <input
                  type="checkbox"
                  className="w-4 h-4 accent-amber-500"
                  checked={form.attachment_required}
                  disabled={!form.allow_attachment}
                  onChange={(e) =>
                    setForm({ ...form, attachment_required: e.target.checked })
                  }
                />
              </label>
            </div>
          </div>
          <ReferenceEvidenceAdminPanel
            formKey={formKey}
            questionId={editing?.id}
          />
          {formKey === "gap_assessment" && (
            <GapPolicyLinkFields
              form={form}
              setForm={setForm}
              storedPolicies={linkedPolicies}
            />
          )}
          <Button onClick={save} loading={saving}>
            {editing ? "Save Changes" : "Create Question"}
          </Button>
        </div>
      </Modal>

      <Modal
        open={!!viewQuestion}
        onClose={() => setViewQuestion(null)}
        title="Question Details"
      >
        <div className="space-y-4">
          <div>
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
              Question Text
            </span>
            <p className="mt-1.5 text-base text-slate-800 font-medium whitespace-pre-wrap break-words">
              {viewQuestion}
            </p>
          </div>
          <div className="flex justify-end pt-2">
            <Button onClick={() => setViewQuestion(null)}>Close</Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={!!viewDescription}
        onClose={() => setViewDescription(null)}
        title="Description"
      >
        <div className="space-y-4">
          <div>
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
              Full Description
            </span>
            <p className="mt-1.5 text-base text-slate-700 whitespace-pre-wrap break-words">
              {viewDescription}
            </p>
          </div>
          <div className="flex justify-end pt-2">
            <Button onClick={() => setViewDescription(null)}>Close</Button>
          </div>
        </div>
      </Modal>

      <QuestionActivityLogModal
        open={!!activityQ}
        onClose={() => setActivityQ(null)}
        questionId={activityQ?.id}
        questionText={activityQ?.text}
      />

      {showEvidenceRows && (
        <ReferenceEvidenceViewModal
          open={!!evidenceViewRow}
          onClose={() => setEvidenceViewRow(null)}
          questionLabel={evidenceViewRow ? getDisplayId(evidenceViewRow) : ""}
          catalogItem={evidenceViewItem}
          loading={evidenceCatalogLoading && !evidenceViewItem}
          regenerating={regeneratingEvidenceId === evidenceViewItem?.question_uuid}
          onRegenerate={
            evidenceViewItem?.question_uuid
              ? () => regenerateEvidence(evidenceViewItem)
              : null
          }
          pendingSaveHint={
            meta.using_defaults
              ? "Import defaults to create stored evidence for built-in questions."
              : "No stored evidence saved for this question yet."
          }
        />
      )}
    </>
  );
}

function ScoringLevelsPanel({ formKey, inline = false }) {
  const { showNotification } = useApp();
  const [bands, setBands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const panelRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch(
        `/tenant/form-questions/${formKey}/scoring-config`,
      );
      if (!res.ok) throw new Error("Failed to load scoring config");
      const data = await res.json();
      setBands(data.level_bands || []);
    } catch (e) {
      showNotification(e.message || "Could not load scoring levels", "error");
    } finally {
      setLoading(false);
    }
  }, [formKey, showNotification]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!inline || !expanded) return undefined;
    const onPointerDown = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setExpanded(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [inline, expanded]);

  const addBand = () => {
    setBands([...bands, { min_score: 0, label: "" }]);
  };

  const save = async () => {
    setSaving(true);
    try {
      const cleaned = bands
        .map((b) => ({
          min_score: Number(b.min_score) || 0,
          label: String(b.label || "").trim(),
        }))
        .filter((b) => b.label);
      const res = await apiFetch(
        `/tenant/form-questions/${formKey}/scoring-config`,
        {
          method: "PUT",
          body: JSON.stringify({ level_bands: cleaned }),
        },
      );
      if (!res.ok) throw new Error(await parseApiError(res, "Save failed"));
      const data = await res.json();
      setBands(data.level_bands || []);
      showNotification("Scoring levels saved", "success");
    } catch (e) {
      showNotification(e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  const levelType =
    formKey === "pia" ? "PIA Risk Levels" : "ROPA Compliance Levels";

  const editorBody = (
    <>
      <p className="text-[11px] text-slate-500 leading-snug">
        Map scores (0–100) to {levelType.toLowerCase()}. Highest matching
        minimum score wins.
      </p>
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button type="button" variant="secondary" size="sm" onClick={addBand}>
          Add Band
        </Button>
        <Button type="button" size="sm" onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save Levels"}
        </Button>
      </div>
      {bands.length === 0 ? (
        <p className="text-xs text-slate-500 italic py-1">No bands configured yet.</p>
      ) : (
        <div className="max-h-44 overflow-y-auto space-y-1.5 pr-0.5">
          {bands.map((band, idx) => (
            <div
              key={idx}
              className="grid grid-cols-[3.5rem_1fr_auto] sm:grid-cols-[4rem_auto_1fr_auto] items-center gap-1.5"
            >
              <input
                type="number"
                min="0"
                max="100"
                className="w-full border border-slate-200 rounded-lg px-2 py-1 text-xs text-center"
                value={band.min_score}
                onChange={(e) => {
                  const next = [...bands];
                  next[idx] = {
                    ...next[idx],
                    min_score: Number(e.target.value),
                  };
                  setBands(next);
                }}
                placeholder="Min"
                aria-label="Minimum score"
              />
              <span className="text-[10px] text-slate-400 text-center hidden sm:block">
                % →
              </span>
              <input
                className="col-span-2 sm:col-span-1 w-full border border-slate-200 rounded-lg px-2 py-1 text-xs"
                value={band.label}
                onChange={(e) => {
                  const next = [...bands];
                  next[idx] = { ...next[idx], label: e.target.value };
                  setBands(next);
                }}
                placeholder="Level label"
                aria-label="Level label"
              />
              <button
                type="button"
                onClick={() => setBands(bands.filter((_, i) => i !== idx))}
                className="text-[11px] font-medium text-red-500 hover:text-red-700 px-1.5 py-1"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </>
  );

  if (inline) {
    return (
      <div ref={panelRef} className="relative">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className={`inline-flex items-center gap-1.5 text-sm border rounded-xl px-3 py-2 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all whitespace-nowrap ${
            expanded
              ? "border-indigo-300 ring-2 ring-indigo-100"
              : "border-slate-200 hover:border-slate-300"
          }`}
          aria-expanded={expanded}
          title="Configure score level bands"
        >
          <SlidersHorizontal size={14} className="text-slate-500 shrink-0" />
          Score Bands
          <span className="text-xs text-slate-400 tabular-nums">({bands.length})</span>
          <ChevronDown
            size={14}
            className={`text-slate-400 shrink-0 transition-transform ${
              expanded ? "rotate-180" : ""
            }`}
          />
        </button>
        {expanded && (
          <div className="absolute left-0 sm:left-auto sm:right-0 top-[calc(100%+6px)] z-30 w-[min(calc(100vw-2.5rem),22rem)] sm:w-96 bg-white border border-slate-200 rounded-xl shadow-lg p-3 space-y-2">
            <p className="text-xs font-semibold text-slate-800">Score Level Bands</p>
            {editorBody}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="mb-3 rounded-xl border border-slate-200 bg-white overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-slate-50/80 transition-colors"
        aria-expanded={expanded}
      >
        <div className="min-w-0">
          <span className="text-sm font-semibold text-slate-800">
            Score Level Bands
          </span>
          <span className="text-xs text-slate-500 ml-2">
            {bands.length} band{bands.length === 1 ? "" : "s"} · {levelType}
          </span>
        </div>
        <ChevronDown
          size={16}
          className={`text-slate-400 flex-shrink-0 transition-transform ${
            expanded ? "rotate-180" : ""
          }`}
        />
      </button>

      {expanded && (
        <div className="px-3 pb-3 pt-2 border-t border-slate-100 space-y-2">
          {editorBody}
        </div>
      )}
    </div>
  );
}

export default function FormQuestionsManager() {
  const { showNotification, currentUser } = useApp();
  const { formKey: formKeyParam } = useParams();
  const navigate = useNavigate();
  const panelActionsRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [showDeletedLogs, setShowDeletedLogs] = useState(false);
  const formKey = FORM_KEYS.includes(formKeyParam)
    ? formKeyParam
    : "gap_assessment";
  const activeMeta = FORM_META[formKey];
  const allowedModules = currentUser?.modules || [];
  const isFormLocked = (key) => !allowedModules.includes(FORM_KEY_MODULE[key]);

  const registerPanelActions = useCallback((actions) => {
    panelActionsRef.current = actions;
  }, []);

  useEffect(() => {
    if (formKeyParam && !FORM_KEYS.includes(formKeyParam)) {
      navigate("/tenant/form-questions/gap_assessment", { replace: true });
    }
  }, [formKeyParam, navigate]);

  const switchForm = (key) => {
    if (isFormLocked(key)) return;
    navigate(`/tenant/form-questions/${key}`);
  };

  const handleImportDefaults = useCallback(async () => {
    const label = FORM_META[formKey]?.label || formKey;
    const ok = await confirmAction({
      title: `Import default ${label} questions?`,
      text: "Platform default questions will be added to your tenant. Existing custom questions are not removed.",
      confirmText: "Import",
      icon: "question",
    });
    if (!ok) return;
    setImporting(true);
    try {
      const endpoint =
        formKey === "tprm"
          ? "/tenant/questionnaire/import-defaults"
          : `/tenant/form-questions/${formKey}/import-defaults`;

      const res = await apiFetch(endpoint, { method: "POST" });

      if (res.status === 409) {
        showNotification(
          "Default questions already exist. Generating any missing AI evidence.",
          "info",
        );

        await panelActionsRef.current?.refresh?.();
        return;
      }

      if (!res.ok) {
        throw new Error(await parseApiError(res, "Import failed"));
      }

      const data = await res.json();

      showNotification(
        data.message ||
          "Defaults imported. AI evidence is generating for each question.",
        "success",
      );

      await panelActionsRef.current?.refresh?.();
    } catch (e) {
      showNotification(e.message || "Import failed", "error");
    }
  }, [formKey, showNotification]);

  const handleAddQuestion = () => panelActionsRef.current?.openCreate?.();

  return (
    <div className="space-y-6 animate-fade-in">
      <div data-tour-id="tour-form-header" className="flex items-start justify-between gap-3 sm:gap-4">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-slate-600 to-indigo-700 flex items-center justify-center shadow-md flex-shrink-0">
            <ListChecks size={22} className="text-white" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900">
              Form Questions
            </h1>
            <p className="text-xs sm:text-sm text-slate-500 mt-0.5 sm:mt-1 leading-snug">
              Manage questions for Gap Assessment, ROPA, PIA, and TPRM — all in
              one place
            </p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5 sm:flex-row sm:flex-wrap sm:justify-end shrink-0">
          <Button
            data-tour-id="tour-form-deleted"
            variant="secondary"
            size="sm"
            onClick={() => setShowDeletedLogs(true)}
            className="border-red-200 text-red-600 bg-red-50 hover:bg-red-100 shadow-sm whitespace-nowrap"
            title="Deleted Questions Log"
          >
            <Trash2 size={14} className="text-red-500 shrink-0" />
            Deleted Log
          </Button>
          <Button
            data-tour-id="tour-form-import"
            variant="secondary"
            size="sm"
            onClick={handleImportDefaults}
            disabled={importing || isFormLocked(formKey)}
            className="whitespace-nowrap"
            title="Import default questions"
          >
            <Upload size={15} className="text-blue-600 shrink-0" />
            {importing ? "Importing…" : "Import Defaults"}
          </Button>
          <Button
            data-tour-id="tour-form-bulk"
            variant="secondary"
            size="sm"
            onClick={() => setShowBulk(true)}
            disabled={isFormLocked(formKey)}
            className="whitespace-nowrap"
            title="Bulk upload questions"
          >
            <Upload size={15} className="text-indigo-600 shrink-0" />
            Bulk Upload
          </Button>
          <Button
            data-tour-id="tour-form-add"
            size="sm"
            onClick={handleAddQuestion}
            disabled={isFormLocked(formKey)}
            className="whitespace-nowrap"
            title="Add question"
          >
            <Plus size={15} className="text-white shrink-0" />
            Add Ques
          </Button>
        </div>
      </div>

      <div
        data-tour-id="tour-form-questions"
        className="flex gap-2 overflow-x-auto pb-1"
      >
        {FORM_KEYS.map((key) => {
          const meta = FORM_META[key];
          const TabIcon = meta.icon;
          const active = formKey === key;
          const locked = isFormLocked(key);
          return (
            <button
              key={key}
              type="button"
              onClick={() => switchForm(key)}
              disabled={locked}
              title={
                locked
                  ? `${meta.label} is not included in your license`
                  : undefined
              }
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition ${
                locked
                  ? "bg-slate-50 text-slate-400 border border-slate-200 cursor-not-allowed opacity-70"
                  : active
                    ? "bg-indigo-600 text-white shadow-sm"
                    : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
              }`}
            >
              <TabIcon
                size={15}
                className={
                  locked
                    ? "text-slate-400"
                    : active
                      ? "text-white"
                      : meta.tabIconColor
                }
              />
              {meta.label}
            </button>
          );
        })}
      </div>

      {isFormLocked(formKey) ? (
        <div className="relative overflow-hidden rounded-2xl border border-indigo-100 bg-gradient-to-b from-indigo-50/70 via-white to-white py-16 px-6 flex flex-col items-center justify-center gap-3 text-center">
          <div className="pointer-events-none absolute -top-16 -right-16 w-56 h-56 rounded-full bg-gradient-to-br from-indigo-200/40 to-fuchsia-200/30 blur-2xl" />
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 via-violet-500 to-fuchsia-500 flex items-center justify-center shadow-lg shadow-indigo-500/30 ring-4 ring-white/60">
            <Crown size={20} className="text-white" strokeWidth={2.25} />
          </div>
          <div className="relative space-y-1">
            <p className="text-sm font-semibold text-slate-800">
              {activeMeta?.label} isn&apos;t included in your plan
            </p>
            <p className="text-xs text-slate-500 max-w-sm mx-auto leading-relaxed">
              Upgrade your organization&apos;s license to manage {activeMeta?.label}{" "}
              questions and unlock this module for your team.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/tenant/license")}
            className="relative group inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold text-white bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 shadow-md shadow-indigo-200/80 transition-all"
          >
            Upgrade License
            <ArrowUpRight
              size={13}
              className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            />
          </button>
        </div>
      ) : activeMeta?.isTprm ? (
        <Suspense
          fallback={
            <p className="text-sm text-slate-500 py-8 text-center">
              Loading TPRM questions…
            </p>
          }
        >
          <div data-tour-id="tour-form-list">
            <Questionnaire embedded onRegisterActions={registerPanelActions} />
          </div>
        </Suspense>
      ) : (
        <div data-tour-id="tour-form-list">
          <StandardFormPanel
            formKey={formKey}
            onRegisterActions={registerPanelActions}
          />
        </div>
      )}

      <FormQuestionsBulkUploadModal
        open={showBulk}
        onClose={() => setShowBulk(false)}
        formKey={formKey}
        sections={FORM_SECTIONS[formKey] || []}
        onSuccess={() => panelActionsRef.current?.refresh?.()}
      />

      <DeletedQuestionsLogModal
        open={showDeletedLogs}
        onClose={() => setShowDeletedLogs(false)}
      />
    </div>
  );
}
